import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { from, iif, NEVER, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { getRedirectPnrOrUndefined, PassengerNameRecord } from '@fcom/common-booking';
import { finShare } from '@fcom/rx';
import { AppState, isNotBlank, unsubscribe } from '@fcom/core';
import { RedirectActions } from '@fcom/common-booking/store/actions/redirect.actions';

@Injectable()
export class PnrFormService implements OnDestroy {
  subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store$: Store<AppState>,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  getInitialValues(): { initialValues$; deepLinkRouteChange$ } {
    if (isPlatformServer(this.platform)) {
      return {
        initialValues$: of({
          lastName: '',
          recLoc: '',
        }),
        deepLinkRouteChange$: NEVER,
      };
    }
    const initialStoreValues$: Observable<PassengerNameRecord> = this.store$.pipe(
      getRedirectPnrOrUndefined(),
      map((pnr: PassengerNameRecord) => ({
        lastName: pnr?.lastName ?? '',
        recLoc: pnr?.recLoc ?? '',
      })),
      take(1),
      finShare()
    );

    this.subscription.add(
      initialStoreValues$
        .pipe(filter((pnr) => Boolean(pnr?.recLoc)))
        .subscribe(() => this.store$.dispatch(RedirectActions.clearPNR()))
    );

    const initialValues$: Observable<PassengerNameRecord> = this.route.queryParams.pipe(
      switchMap((params) => {
        return iif(
          () => isNotBlank(params['recLoc']) && isNotBlank(params['lastName']),
          of({
            lastName: decodeURI(params['lastName']),
            recLoc: params['recLoc'],
          }),
          initialStoreValues$
        );
      }),
      take(1),
      finShare()
    );

    const deepLinkRouteChange$ = initialValues$.pipe(
      take(1),
      filter((values) => isNotBlank(values.recLoc) && isNotBlank(values.lastName)),
      switchMap((record) =>
        from(
          this.router.navigate([], {
            queryParams: { lastName: null, recLoc: null },
            queryParamsHandling: 'merge',
          })
        ).pipe(map(() => record))
      )
    );

    return {
      initialValues$,
      deepLinkRouteChange$,
    };
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }
}
